// client/src/App.js
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./styles/heroTypography.css";
import "./components/global.css";
import useRouteScrollReset from "./hooks/useRouteScrollReset";

import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetPassword from "./pages/SetPassword";
import Dashboard from "./pages/Dashboard";
import MetaAnalytics from "./pages/MetaAnalytics";
import Ga4Analytics from "./pages/Ga4Analytics";
import FacebookAnalytics from "./pages/FacebookAnalytics";
import InstagramAnalytics from "./pages/InstagramAnalytics";
import TikTokAnalytics from "./pages/TikTokAnalytics";
import YouTubeAnalytics from "./pages/YouTubeAnalytics";
import XAnalytics from "./pages/XAnalytics";
import GoogleAnalytics from "./pages/GoogleAnalytics";
import ChatGPTSearchAnalytics from "./pages/ChatGPTSearchAnalytics";
import AmazonAnalytics from "./pages/AmazonAnalytics";
import YahooAnalytics from "./pages/YahooAnalytics";
import ShopifyAnalytics from "./pages/ShopifyAnalytics";
import LinkedInAnalytics from "./pages/LinkedInAnalytics";
import ProgrammaticAnalytics from "./pages/ProgrammaticAnalytics";
import ClientPlatformAnalytics from "./pages/ClientPlatformAnalytics";
import PublicPlatformReport from "./pages/PublicPlatformReport";
import PublicFundingRequest from "./pages/PublicFundingRequest";
import PublicPlatformConnect from "./pages/PublicPlatformConnect";
import AudioAnalytics from "./pages/AudioAnalytics";
import InGameAnalytics from "./pages/InGameAnalytics";
import CTVAnalytics from "./pages/CTVAnalytics";
import VideoAnalytics from "./pages/VideoAnalytics";
import MobileAnalytics from "./pages/MobileAnalytics";
import DisplayAnalytics from "./pages/DisplayAnalytics";
import DOOHAnalytics from "./pages/DOOHAnalytics";
import EmailAnalytics from "./pages/EmailAnalytics";
import CinemaAnalytics from "./pages/CinemaAnalytics";
import AutoIntelligence from "./pages/AutoIntelligence";
import RetailIntelligence from "./pages/RetailIntelligence";
import GeneralIntelligence from "./pages/GeneralIntelligence";
import ClientAIOptimizations from "./pages/ClientAIOptimizations";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";

import Loading from "./components/Loading";
import AuthLoading from "./components/AuthLoading";
import ProtectedRouteClient from "./components/ProtectedRouteClient";
import CreativeAssets from "./pages/CreativeAssets";
import BetaSurfaceRoute, {
  useBetaSurfaceHistoryGuard,
} from "./components/BetaSurfaceRoute";
import ErrorBoundary from "./components/ErrorBoundary";

import TruthSocialAnalytics from "./pages/TruthSocialAnalytics";
import RumbleAnalytics from "./pages/RumbleAnalytics";
import RedditAnalytics from "./pages/RedditAnalytics";

import SherlocHubClient from "./pages/SherlocHubClient";
import SherlocReports from "./pages/SherlocReports";
import PantheonPage from "./pages/PantheonPage";

import Integrations from "./pages/Integrations";
import CreativeStudio from "./pages/CreativeStudio";
import ClientApprovals from "./pages/ClientApprovals";
import SherlocMediaPlannerClient from "./pages/SherlocMediaPlanner";
import CampaignStrategyMicrosite from "./pages/CampaignStrategyMicrosite";
import Billing from "./pages/Billing";
import SherlocMediaCampaigns from "./pages/SherlocMediaCampaigns";
import InstallApp from "./pages/InstallApp";
import CampaignManagement from "./pages/CampaignManagement";
import FlightRhythmReporting from "./pages/FlightRhythmReporting";
import Optimus from "./pages/Optimus";
import ProfileHub from "./pages/ProfileHub";
import GenesisHub from "./pages/GenesisHub";
import NexusHub from "./pages/NexusHub";

import MetricoolAnalytics from "./pages/MetricoolAnalytics";

import SherlocAuthorizations from "./pages/SherlocAuthorizations";
import AuthorizeReturn from "./pages/AuthorizeReturn";

import { SherlocVoiceProvider } from "./voice/VoiceContext";
import { createPortal } from "react-dom";
import GlobalVoiceController from "./components/GlobalVoiceController";

import { useAdvertiser } from "./contexts/AdvertiserContext";
import { useAuth } from "./contexts/AuthContext";
import { PageAwarenessProvider } from "./contexts/PageAwarenessContext";
import useGlobalSwipeNavigation from "./hooks/useGlobalSwipeNavigation";
import { resolveGovernedSwipeRouteOrder } from "./config/swipeRouteOrder";
import { withoutProtectedBetaRoutes } from "./config/betaSurfaceAccess";
import VerticalIntelligenceRoute from "./components/VerticalIntelligenceRoute";
import {
  classifyRouteActivity,
  installDashboardActivityClickTracking,
  trackDashboardActivity,
} from "./utils/dashboardActivityTracker";
import { shouldShowOneBackButton } from "./utils/oneBackNavigation";
import { shouldShowDashboardBrain } from "./utils/dashboardBrainNavigation";
import {
  isCampaignStrategyMicrositePath as isStrategyMicrositeAddressPath,
  micrositeReturnStateMatches,
} from "./utils/campaignStrategyMicrositeAddress";

import edenContextBridge from "./eden/context/EdenContextBridge";

const SUBSEQUENT_DASHBOARD_LOADING_MS = 600;
const SKIP_DASHBOARD_OVERLAY_ONCE = "cortexSkipDashboardOverlayOnce";

const ENTER_DASH_FROM_LOGIN_KEY = "cortexEnterDashboardFromLogin";
const DASH_READY_KEY = "cortexDashboardReady";

const SOFT_MIN_MS = 450;
const SOFT_MAX_MS = 7000;

const ROUTE_OVERLAY_MS = 450;
const ROUTE_LOADER_FAILSAFE_MS = 9000;
// Canonical route-overlay exclusion contract. Paths here own their own first
// frame: the generic overlay is a full-screen rgba(0, 0, 0, 0.82) wash held for
// ROUTE_OVERLAY_MS and then faded out over LOADER_FADE_MS, which on a dark,
// glow-heavy hub reads as the page landing with a wrong tint and settling.
const ROUTE_OVERLAY_EXCLUDE_PATHS = new Set(["/settings", "/genesis-hub"]);

// Mirrors the repo's existing pathname normalization (see shouldHideVoice).
const normalizeOverlayPath = (pathname) =>
  String(pathname || "")
    .toLowerCase()
    .trim()
    .replace(/\/+$/, "") || "/";

const LOADER_FADE_MS = 420;
const KEY_SELECTED_ADV_ID = "selectedAdvertiserId";
const KEY_SELECTED_ADV_NAME = "selectedAdvertiserName";
const LEGACY_SELECTED_ADV_NAME = "selectedAdvertiser";

// ✅ Auth transition flag
const AUTH_TRANSITION_KEY = "cortexAuthTransition";

// ✅ Logout guard flag (MOBILE SAFE)
const LOGOUT_IN_PROGRESS_KEY = "cortexLogoutInProgress";
const LOGOUT_MODE_KEY = "cortexLogoutMode";

// ✅ Recent logout lock (matches AuthContext)
const LAST_LOGOUT_TS_KEY = "cortexLastLogoutTs";

// ✅ Client-only auth keys (match AuthContext)
const CLIENT_TOKEN_KEY = "clientAuthToken";
const CLIENT_AUTH_STATE_KEY = "cortexClientAuthState";
const CREATIVE_PORTAL_PATH = "/nexus-hub";

const MICROSITE_FALLBACK_ROUTE = "/sherloc-media-planner";
const MICROSITE_RETURN_STATE_SESSION_KEY = "cphere:sherloc:microsite-return-state";

// A legacy /campaign-strategy/{identifier} link or the readable
// /campaign-strategy/{advertiser}/{campaign} address.
const isCampaignStrategyMicrositePath = (pathname = "") => isStrategyMicrositeAddressPath(pathname);

// Public, unauthenticated analytics report microsite. The token in the path is
// the entire credential; the page carries no app chrome and no route out of
// itself, so it is treated as public everywhere the app asks.
const isPublicPlatformReportPath = (pathname = "") =>
  /^\/report\/[^/]+\/?$/.test(String(pathname || ""));

// Public, unauthenticated media campaign funding request. Same contract as
// the public report: the token in the path is the entire credential, the page
// carries no app chrome and no route out of itself, so it is treated as
// public everywhere the app asks.
const isPublicFundingRequestPath = (pathname = "") =>
  /^\/fund\/[^/]+\/?$/.test(String(pathname || ""));

// Public, unauthenticated platform connection page. Same contract as the
// public report and the funding request: the token in the path is the entire
// credential, the page carries no app chrome and no route out of itself, so
// it is treated as public everywhere the app asks.
const isPublicPlatformConnectPath = (pathname = "") =>
  /^\/connect\/[^/]+\/?$/.test(String(pathname || ""));

const sanitizeInternalReturnPath = (value, fallback = MICROSITE_FALLBACK_ROUTE) => {
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return fallback;
  if (/^\/(?:login|forgot-password|reset-password|set-password|authorize\/return)(?:\/|$)/i.test(raw)) {
    return fallback;
  }
  return raw;
};

const readMicrositeReturnState = (pathname = "") => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(MICROSITE_RETURN_STATE_SESSION_KEY) || "{}");
    if (!stored || typeof stored !== "object") return {};
    return micrositeReturnStateMatches(stored, { pathname }) ? stored : {};
  } catch {
    return {};
  }
};

const normalizePortalRole = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[_\s]/g, "");

const getLandingPathForRole = (value) =>
  normalizePortalRole(value) === "creative" ? CREATIVE_PORTAL_PATH : "/dashboard";

// ✅ MOBILE SAFE: check BOTH storages
const getFlagBoth = (key) => {
  try {
    if (localStorage.getItem(key) === "1") return true;
  } catch {}
  try {
    if (sessionStorage.getItem(key) === "1") return true;
  } catch {}
  return false;
};

const isLoggingOut = () => getFlagBoth(LOGOUT_IN_PROGRESS_KEY);
const isLogoutMode = () => getFlagBoth(LOGOUT_MODE_KEY);

const setLogoutGuard = () => {
  try {
    localStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "1");
  } catch {}
  try {
    sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "1");
  } catch {}
};

const setLogoutMode = () => {
  try {
    localStorage.setItem(LOGOUT_MODE_KEY, "1");
  } catch {}
  try {
    sessionStorage.setItem(LOGOUT_MODE_KEY, "1");
  } catch {}
};

const clearLogoutFlags = () => {
  // NOTE: We do NOT clear LAST_LOGOUT_TS_KEY here; AuthContext clears it after successful login.
  try {
    localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  } catch {}
  try {
    sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  } catch {}
  try {
    localStorage.removeItem(LOGOUT_MODE_KEY);
  } catch {}
  try {
    sessionStorage.removeItem(LOGOUT_MODE_KEY);
  } catch {}
};

const App = () => (
  <SherlocVoiceProvider>
    <PageAwarenessProvider>
      <AppWithLoading />
    </PageAwarenessProvider>
  </SherlocVoiceProvider>
);

// Keep wrapper
const DashboardRouteWrapper = ({ children }) => children;

const AppWithLoading = () => {
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeLoadingSource, setRouteLoadingSource] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  useRouteScrollReset(location.pathname, location.search);

  const { isAuthenticating, isAuthenticated, authToken, email, role, logout } =
    useAuth() || {};
  const landingPath = getLandingPathForRole(role);

  // Covers the transitions a route element never sees: Back/Forward,
  // history.go(), tab restoration, and a page returned from the browser's
  // back/forward cache after a session change.
  useBetaSurfaceHistoryGuard(role);

  const prevPathRef = useRef(location.pathname);
  const softPrevPathRef = useRef(location.pathname);

  const advCtx = useAdvertiser() || {};
  const selectedAdvertiserId = advCtx.selectedAdvertiserId ?? null;
  const selectedAdvertiserName = advCtx.selectedAdvertiserName ?? null;
  const selectedAdvertiserVertical = advCtx.selectedAdvertiserVertical ?? "general";
  const activityAdvertiserRef = useRef("");

  // ✅ local state latch so logout mode is instant (prevents flashing)
  const [logoutModeState, setLogoutModeState] = useState(() => {
    try {
      return isLogoutMode() || isLoggingOut();
    } catch {
      return false;
    }
  });

  // Keep it in sync if storage changes (only ever latches ON; cleared on successful auth below)
  useEffect(() => {
    const current = isLogoutMode() || isLoggingOut();
    if (current && !logoutModeState) setLogoutModeState(true);
  }, [logoutModeState]);

  const logoutMode = isLogoutMode() || isLoggingOut();
  const effectiveLogoutMode = logoutModeState || logoutMode;

  const isPublic =
    location.pathname === "/login" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/set-password" ||
    location.pathname === "/authorize/return" ||
    location.pathname === "/pantheon" ||
    isCampaignStrategyMicrositePath(location.pathname) ||
    isPublicPlatformReportPath(location.pathname) ||
    isPublicFundingRequestPath(location.pathname) ||
    isPublicPlatformConnectPath(location.pathname);

  const setRouteLoader = useCallback((nextLoading, source = "unknown") => {
    setRouteLoading(Boolean(nextLoading));
    setRouteLoadingSource(nextLoading ? source : "");
  }, []);

  // Gesture navigation resolves previous/next against the governed sequence
  // for the selected advertiser's vertical — never the raw canonical order —
  // so an ineligible vertical page can never be a swipe destination. Switching
  // advertisers recomputes this immediately via the context vertical.
  // withoutProtectedBetaRoutes is belt-and-braces over the sequence itself:
  // even if a protected surface is reintroduced upstream, gesture navigation
  // can never offer it as a previous/next destination.
  const governedSwipeRoutes = useMemo(
    () =>
      withoutProtectedBetaRoutes(
        resolveGovernedSwipeRouteOrder(selectedAdvertiserVertical)
      ),
    [selectedAdvertiserVertical]
  );
  useGlobalSwipeNavigation({
    disabled: isPublic || routeLoading,
    routes: governedSwipeRoutes,
  });

  // Same path normalization the voice-visibility check already uses: the
  // router matches `/genesis-hub/` and `/Genesis-Hub` to the same route, and
  // `location.pathname` never carries the query string, so the exclusion has
  // to compare on the normalized pathname or a trailing slash silently puts
  // the wash back. Keyed on pathname alone, it holds for direct loads,
  // internal navigation and history POP alike.
  const suppressRouteOverlay = ROUTE_OVERLAY_EXCLUDE_PATHS.has(
    normalizeOverlayPath(location.pathname)
  );

  const shouldHideVoice = useMemo(() => {
    const p =
      String(location.pathname || "")
        .toLowerCase()
        .trim()
        .replace(/\/+$/, "") || "/";

    if (p === "/login") return true;
    if (p === "/forgot-password") return true;
    if (p === "/reset-password") return true;
    if (p === "/set-password") return true;
    if (p === "/authorize/return") return true;
    if (p.startsWith("/authorize/return/")) return true;
    if (p === "/pantheon") return true;
    // Public/shareable campaign strategy microsite: Eden Ask (bottom-right orb
    // and centered entry overlay) is for authenticated app users only.
    if (isCampaignStrategyMicrositePath(location.pathname)) return true;
    // Public analytics report: Eden Ask is for authenticated app users only.
    if (isPublicPlatformReportPath(location.pathname)) return true;
    // Public funding request: Eden Ask is for authenticated app users only.
    if (isPublicFundingRequestPath(location.pathname)) return true;
    // Public platform connection: Eden Ask is for authenticated app users only.
    if (isPublicPlatformConnectPath(location.pathname)) return true;

    return false;
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.history) return undefined;
    const previousValue = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = "manual";
    } catch {}

    return () => {
      try {
        window.history.scrollRestoration = previousValue || "auto";
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[App][route-loader]", {
        event: "pathname_change",
        pathname: location.pathname,
        search: location.search,
        routeLoading,
        routeLoadingSource,
        authLoading: Boolean(isAuthenticating),
        authenticated: Boolean(isAuthenticated),
      });
    }

    setRouteLoader(false, "route_change_reset");
  // Run only on navigation changes; route overlay effects below may opt back in.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname, location.search]);

  useEffect(() => {
    if (!routeLoading) return undefined;

    const timer = window.setTimeout(() => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[App][route-loader]", {
          event: "failsafe_clear",
          pathname: location.pathname,
          source: routeLoadingSource,
        });
      }
      setRouteLoader(false, "failsafe");
    }, ROUTE_LOADER_FAILSAFE_MS);

    return () => window.clearTimeout(timer);
  }, [location.pathname, routeLoading, routeLoadingSource, setRouteLoader]);

  // ✅ CRITICAL: if we become authenticated, clear logout latch + flags immediately
  // This fixes the “can’t login” loop after a logout-mode session.
  useEffect(() => {
    if (!isAuthenticated) return;

    // release local latch
    if (logoutModeState) setLogoutModeState(false);

    // clear persistent flags so routes stop forcing /login
    clearLogoutFlags();
  }, [isAuthenticated, logoutModeState]);

  // Eden bridge
  useEffect(() => {
    edenContextBridge.setPathname(location.pathname);

    const selectedDateStart = localStorage.getItem("selectedDateStart");
    const selectedDateEnd = localStorage.getItem("selectedDateEnd");

    edenContextBridge.setExternal({
      role: String(role || "client").toLowerCase(),
      token: authToken || null,
      email: email || null,
      advertiserId: selectedAdvertiserId,
      advertiserName: selectedAdvertiserName,
      advertiserVertical: selectedAdvertiserVertical,
      dateStart: selectedDateStart || null,
      dateEnd: selectedDateEnd || null,
    });
  }, [
    location.pathname,
    selectedAdvertiserId,
    selectedAdvertiserName,
    selectedAdvertiserVertical,
    authToken,
    email,
    role,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isPublic || effectiveLogoutMode || isAuthenticating) return;
    const routeActivity = classifyRouteActivity(location.pathname);
    trackDashboardActivity({
      ...routeActivity,
      advertiserId: selectedAdvertiserId,
      advertiserName: selectedAdvertiserName,
      metadata: { role: role || "client" },
    });
  }, [
    effectiveLogoutMode,
    isAuthenticated,
    isAuthenticating,
    isPublic,
    location.pathname,
    role,
    selectedAdvertiserId,
    selectedAdvertiserName,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isPublic || effectiveLogoutMode || isAuthenticating) return;
    if (!selectedAdvertiserId && !selectedAdvertiserName) return;

    const key = `${selectedAdvertiserId || ""}:${selectedAdvertiserName || ""}`;
    if (activityAdvertiserRef.current === key) return;
    activityAdvertiserRef.current = key;

    trackDashboardActivity({
      actionType: "advertiser_switched",
      routeSection: location.pathname,
      advertiserId: selectedAdvertiserId,
      advertiserName: selectedAdvertiserName,
      entityType: "advertiser",
      entityId: selectedAdvertiserId || selectedAdvertiserName,
    });
  }, [
    effectiveLogoutMode,
    isAuthenticated,
    isAuthenticating,
    isPublic,
    location.pathname,
    selectedAdvertiserId,
    selectedAdvertiserName,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isPublic || effectiveLogoutMode || isAuthenticating) return undefined;
    return installDashboardActivityClickTracking({
      getContext: () => ({
        routeSection: location.pathname,
        advertiserId: selectedAdvertiserId,
        advertiserName: selectedAdvertiserName,
      }),
    });
  }, [
    effectiveLogoutMode,
    isAuthenticated,
    isAuthenticating,
    isPublic,
    location.pathname,
    selectedAdvertiserId,
    selectedAdvertiserName,
  ]);

  // ✅ If logout-mode: hard-stop all overlays and force /login (no fighting)
  useEffect(() => {
    if (!effectiveLogoutMode) return;
    setRouteLoader(false, "logout_mode");

    // ✅ use logout query consistently (matches AuthContext hard redirect)
    if (
      location.pathname !== "/login" &&
      !isCampaignStrategyMicrositePath(location.pathname)
    ) {
      navigate("/login?logout=1", { replace: true });
    }
  }, [effectiveLogoutMode, location.pathname, navigate, setRouteLoader]);

  // ✅ Auth gate (do nothing during logout-mode)
  useEffect(() => {
    if (effectiveLogoutMode) return;
    if (isPublic) return;
    if (isAuthenticating) return;

    if (!isAuthenticated) {
      setRouteLoader(false, "auth_redirect");
      navigate("/login", { replace: true });
    }
  }, [effectiveLogoutMode, isPublic, isAuthenticating, isAuthenticated, navigate, setRouteLoader]);

  // ✅ Prevent showing login page if authenticated (but NOT in logout-mode)
  useEffect(() => {
    if (effectiveLogoutMode) return;
    if (location.pathname !== "/login") return;
    if (isAuthenticating) return;
    if (!isAuthenticated) return;
    navigate(landingPath, { replace: true });
  }, [
    effectiveLogoutMode,
    navigate,
    location.pathname,
    isAuthenticating,
    isAuthenticated,
    landingPath,
  ]);

  // ✅ Dashboard overlay logic (disabled during logout-mode/public/auth boot)
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currPath = location.pathname;

    if (effectiveLogoutMode || !isAuthenticated || isPublic || isAuthenticating) {
      setRouteLoader(false, "dashboard_overlay_disabled");
      prevPathRef.current = currPath;
      return;
    }

    const enteringDashboard = currPath === "/dashboard" && prevPath !== "/dashboard";

    if (!enteringDashboard) {
      setRouteLoader(false, "dashboard_overlay_not_entering");
      prevPathRef.current = currPath;
      return;
    }

    const skipOnce = sessionStorage.getItem(SKIP_DASHBOARD_OVERLAY_ONCE) === "true";
    if (skipOnce) {
      sessionStorage.removeItem(SKIP_DASHBOARD_OVERLAY_ONCE);
      setRouteLoader(false, "dashboard_overlay_skip_once");
      prevPathRef.current = currPath;
      return;
    }

    const fromLogin = sessionStorage.getItem(ENTER_DASH_FROM_LOGIN_KEY) === "true";

    const runSoftUntilReady = () => {
      let cancelled = false;
      setRouteLoader(true, "dashboard_login_ready_wait");

      const start = Date.now();

      const stop = () => {
        if (cancelled) return;
        setRouteLoader(false, "dashboard_login_ready_wait_done");
      };

      const tick = () => {
        if (cancelled) return;

        const elapsed = Date.now() - start;
        const ready = sessionStorage.getItem(DASH_READY_KEY) === "true";

        if (elapsed < SOFT_MIN_MS) {
          requestAnimationFrame(tick);
          return;
        }

        if (ready || elapsed >= SOFT_MAX_MS) {
          stop();
          return;
        }

        setTimeout(tick, 60);
      };

      tick();

      return () => {
        cancelled = true;
      };
    };

    if (fromLogin) {
      sessionStorage.removeItem(ENTER_DASH_FROM_LOGIN_KEY);
      sessionStorage.setItem(DASH_READY_KEY, "false");

      const cleanup = runSoftUntilReady();
      prevPathRef.current = currPath;
      return cleanup;
    }

    setRouteLoader(true, "dashboard_return");
    const timer = setTimeout(
      () => setRouteLoader(false, "dashboard_return_done"),
      SUBSEQUENT_DASHBOARD_LOADING_MS
    );
    prevPathRef.current = currPath;
    return () => clearTimeout(timer);
  }, [
    effectiveLogoutMode,
    location.pathname,
    isAuthenticated,
    isPublic,
    isAuthenticating,
    setRouteLoader,
  ]);

  // ✅ generic route overlay (disabled during logout-mode/public/auth boot)
  useEffect(() => {
    const prev = softPrevPathRef.current;
    const curr = location.pathname;

    const finish = () => {
      softPrevPathRef.current = curr;
    };

    if (
      effectiveLogoutMode ||
      !isAuthenticated ||
      isPublic ||
      isAuthenticating ||
      suppressRouteOverlay
    ) {
      setRouteLoader(false, "route_overlay_disabled");
      finish();
      return;
    }

    if (curr === "/dashboard") {
      finish();
      return;
    }

    if (curr === prev) return;

    setRouteLoader(true, "route_overlay");
    const t = setTimeout(() => setRouteLoader(false, "route_overlay_done"), ROUTE_OVERLAY_MS);
    finish();

    return () => clearTimeout(t);
  }, [
    effectiveLogoutMode,
    location.pathname,
    isAuthenticated,
    isPublic,
    isAuthenticating,
    suppressRouteOverlay,
    setRouteLoader,
  ]);

  useLayoutEffect(() => {
    document.body.setAttribute("data-route", location.pathname);
    document.documentElement.setAttribute("data-route", location.pathname);
  }, [location.pathname]);

  const handleBack = () => navigate(landingPath);
  const handlePreviousPage = () => {
    if (isCampaignStrategyMicrositePath(location.pathname)) {
      const routeState = location.state || {};
      const storedReturnState = readMicrositeReturnState(location.pathname);
      const params = new URLSearchParams(location.search || "");
      const fallbackRoute = sanitizeInternalReturnPath(
        routeState.returnTo || storedReturnState.returnTo || params.get("from"),
        MICROSITE_FALLBACK_ROUTE
      );
      const returnSource = String(
        routeState.returnSource ||
          routeState.source ||
          storedReturnState.returnSource ||
          storedReturnState.source ||
          params.get("returnSource") ||
          ""
      ).trim();
      const returnCampaignId = String(
        routeState.campaignId || storedReturnState.campaignId || params.get("campaignId") || ""
      ).trim();
      const returnMicrositeSlug = String(
        routeState.micrositeSlug ||
          routeState.publicSlug ||
          storedReturnState.micrositeSlug ||
          storedReturnState.publicSlug ||
          ""
      ).trim();
      const shouldPushPlannerReturn =
        returnSource === "sherloc-media-planner" ||
        Boolean(returnCampaignId && fallbackRoute.startsWith(MICROSITE_FALLBACK_ROUTE));

      if (shouldPushPlannerReturn) {
        navigate(fallbackRoute, {
          replace: false,
          state: {
            source: "campaign-strategy-microsite",
            returnSource,
            restoredFromMicrosite: true,
            campaignId: returnCampaignId || null,
            micrositeSlug: returnMicrositeSlug || null,
            publicSlug: returnMicrositeSlug || null,
            advertiserId:
              String(
                routeState.advertiserId ||
                  storedReturnState.advertiserId ||
                  params.get("advertiserId") ||
                  ""
              ).trim() ||
              null,
            advertiserName: String(
              routeState.advertiserName ||
                storedReturnState.advertiserName ||
                params.get("advertiserName") ||
                ""
            ).trim(),
            campaignName: String(
              routeState.campaignName ||
                storedReturnState.campaignName ||
                params.get("campaignName") ||
                ""
            ).trim(),
          },
        });
        return;
      }

      const hasUsableHistory =
        typeof window !== "undefined" &&
        window.history.length > 1 &&
        location.key &&
        location.key !== "default";

      if (hasUsableHistory) {
        navigate(-1);
        return;
      }

      navigate(fallbackRoute, { replace: false });
      return;
    }

    navigate(-1);
  };

  const handleLogout = async () => {
    // ✅ 1) Set persistent flags FIRST (prevents flashing/fighting on mobile)
    setLogoutGuard();
    setLogoutMode();

    // ✅ Stamp last logout time (ProtectedRouteClient/AuthContext uses this lock)
    try {
      localStorage.setItem(LAST_LOGOUT_TS_KEY, String(Date.now()));
    } catch {}
    try {
      sessionStorage.setItem(LAST_LOGOUT_TS_KEY, String(Date.now()));
    } catch {}

    // ✅ 2) Then latch local state
    setLogoutModeState(true);

    // ✅ Kill overlays now
    setRouteLoader(false, "logout_click");

    // ✅ Clear local auth keys immediately (mobile-safe)
    // IMPORTANT: clear BOTH client-only + legacy keys so we don't keep stale tokens.
    try {
      localStorage.removeItem(CLIENT_TOKEN_KEY);
      localStorage.removeItem(CLIENT_AUTH_STATE_KEY);

      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("email");
      localStorage.removeItem("role");

      localStorage.removeItem(KEY_SELECTED_ADV_ID);
      localStorage.removeItem(KEY_SELECTED_ADV_NAME);
      localStorage.removeItem(LEGACY_SELECTED_ADV_NAME);
    } catch {}

    try {
      sessionStorage.removeItem(CLIENT_TOKEN_KEY);
      sessionStorage.removeItem(CLIENT_AUTH_STATE_KEY);
    } catch {}

    // ✅ Clear dashboard flags
    try {
      sessionStorage.removeItem(SKIP_DASHBOARD_OVERLAY_ONCE);
      sessionStorage.removeItem(ENTER_DASH_FROM_LOGIN_KEY);
      sessionStorage.removeItem(DASH_READY_KEY);
      sessionStorage.removeItem(AUTH_TRANSITION_KEY);
    } catch {}

    // ✅ best-effort context logout
    try {
      if (typeof logout === "function") {
        await logout(); // AuthContext performs hard redirect on mobile-safe path
      }
    } catch {}

    edenContextBridge.clearOverrides();
    edenContextBridge.setExternal({
      token: null,
      email: null,
      role: "client",
      advertiserId: null,
      advertiserName: null,
      advertiserVertical: "general",
      dateStart: null,
      dateEnd: null,
    });

    // ✅ IMPORTANT:
    // Do NOT call navigate() here.
    // AuthContext logout owns the redirect (window.location.replace),
    // and a second React navigation causes mobile "seizure" loops.
  };

  const isExcludedPage = isPublic;

  // The upper-left Dashboard brain. Public routes carry no app chrome, but the
  // strategy microsite is public AND routinely read by signed-in users, so the
  // canonical control comes back for them and stays hidden for anonymous
  // viewers who have no dashboard to reach.
  const showDashboardBrain = shouldShowDashboardBrain({
    isExcludedPage,
    isCampaignStrategyMicrosite: isCampaignStrategyMicrositePath(location.pathname),
    isAuthenticated,
  });

  // The floating one-back arrow must only render where tapping it goes to a real
  // previous in-app route — never on the dashboard/home screen, and never as a
  // dead button when there is no valid previous route to return to.
  const showOneBackButton = shouldShowOneBackButton({
    pathname: location.pathname,
    landingPath,
    isExcludedPage,
    locationKey: location.key,
    historyLength: typeof window !== "undefined" ? window.history.length : 0,
  });

  const showOverlay = Boolean(routeLoading && isAuthenticated);
  const [renderOverlay, setRenderOverlay] = useState(showOverlay);

  useEffect(() => {
    if (showOverlay) {
      setRenderOverlay(true);
      return undefined;
    }

    // A route that owns its own first frame must not be revealed through an
    // overlay left fading out from the route we arrived from — that fade is a
    // dim-then-brighten on the destination, not on the page that raised it.
    if (suppressRouteOverlay) {
      setRenderOverlay(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setRenderOverlay(false);
    }, LOADER_FADE_MS);

    return () => window.clearTimeout(timer);
  }, [showOverlay, suppressRouteOverlay]);

  if (isAuthenticating && !effectiveLogoutMode && !isPublic) return <AuthLoading />;

  // ✅ During logout-mode: render only public routes to prevent blinking/fighting
  if (effectiveLogoutMode) {
    return (
      <>
        <ErrorBoundary resetKey={`logout:${location.pathname}`} dashboardPath={landingPath}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/authorize/return" element={<AuthorizeReturn />} />
            <Route path="/campaign-strategy/:campaignId" element={<CampaignStrategyMicrosite />} />
            <Route path="/campaign-strategy/:advertiserSlug/:campaignSlug" element={<CampaignStrategyMicrosite />} />
            <Route path="/report/:publicReportToken" element={<PublicPlatformReport />} />
            <Route path="/fund/:fundingToken" element={<PublicFundingRequest />} />
            <Route path="/connect/:onboardingToken" element={<PublicPlatformConnect />} />
            <Route path="/pantheon" element={<PantheonPage />} />
            <Route path="*" element={<Navigate to="/login?logout=1" replace />} />
          </Routes>
        </ErrorBoundary>
        {!shouldHideVoice && <GlobalVoiceController />}
      </>
    );
  }

  return (
    <>
      <ErrorBoundary resetKey={location.pathname} dashboardPath={landingPath}>
        <>
          {showDashboardBrain && (
            <div className="back-button" onClick={handleBack} title="Dashboard" />
          )}

          {showOneBackButton &&
            createPortal(
              <div
                className="one-back-button"
                onClick={handlePreviousPage}
                title="Previous Page"
              />,
              document.body
            )}

          {!isExcludedPage && (
            <div className="logout-button" onClick={handleLogout} title="Logout" />
          )}

          <Routes>
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? landingPath : "/login"} replace />}
            />

            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />

            <Route path="/authorize/return" element={<AuthorizeReturn />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRouteClient allowedRoles={["client"]}>
                  <DashboardRouteWrapper>
                    <Dashboard />
                  </DashboardRouteWrapper>
                </ProtectedRouteClient>
              }
            />

          <Route
            path="/profile-hub"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ProfileHub />
              </ProtectedRouteClient>
            }
          />

          <Route
            path="/authorize"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <SherlocAuthorizations />
              </ProtectedRouteClient>
            }
          />

          <Route
            path="/social-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ProgrammaticAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/ga4-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <Ga4Analytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/meta-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <MetaAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/facebook-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <FacebookAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/instagram-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <InstagramAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/tiktok-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <TikTokAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/youtube-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <YouTubeAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/x-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <XAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/google-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <GoogleAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/chatgpt-search-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ChatGPTSearchAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/amazon-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <AmazonAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/yahoo-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <YahooAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/shopify-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ShopifyAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/linkedin-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <LinkedInAnalytics />
              </ProtectedRouteClient>
            }
          />

          <Route
            path="/truthsocial-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <TruthSocialAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/rumble-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <RumbleAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/reddit-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <RedditAnalytics />
              </ProtectedRouteClient>
            }
          />

          <Route
            path="/platform-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ClientPlatformAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/programmatic-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ProgrammaticAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/audio-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <AudioAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/ingame-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <InGameAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/ctv-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <CTVAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/community-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <MetricoolAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/video-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <VideoAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/mobile-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <MobileAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/display-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <DisplayAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/dooh-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <DOOHAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/email-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <EmailAnalytics />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/cinema-analytics"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <VerticalIntelligenceRoute routePath="/cinema-analytics">
                  <CinemaAnalytics />
                </VerticalIntelligenceRoute>
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/general-intelligence"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <VerticalIntelligenceRoute routePath="/general-intelligence">
                  <GeneralIntelligence />
                </VerticalIntelligenceRoute>
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/travel-intelligence"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <VerticalIntelligenceRoute routePath="/travel-intelligence">
                  <GeneralIntelligence vertical="travel_hospitality" />
                </VerticalIntelligenceRoute>
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/auto-intelligence"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <VerticalIntelligenceRoute routePath="/auto-intelligence">
                  <AutoIntelligence />
                </VerticalIntelligenceRoute>
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/retail-intelligence"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <VerticalIntelligenceRoute routePath="/retail-intelligence">
                  <RetailIntelligence />
                </VerticalIntelligenceRoute>
              </ProtectedRouteClient>
            }
          />

          <Route
            path="/sherloc-ai"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <SherlocHubClient />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/campaign-management"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <CampaignManagement />
              </ProtectedRouteClient>
            }
          />
          {/* Client-authorized Flight Rhythm reporting. The Creative Execution
              page also mounts this panel, but that page is a protected Beta
              surface (CREATIVE_BETA_INTERNAL_ACCESS) and is never imported
              here, so this is the only route where a Cortex client reaches
              the panel. URL-only: no hub tile (hub geometry is parity-pinned). */}
          <Route
            path="/flight-rhythm"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <FlightRhythmReporting />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/genesis-hub"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <GenesisHub />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/nexus-hub"
            element={
              <ProtectedRouteClient allowedRoles={["client", "creative"]}>
                <NexusHub />
              </ProtectedRouteClient>
            }
          />
          {/* Client-authorized creative library: governed creatives with their
              attributed delivery per platform. Not a Beta surface — it renders
              none of the trafficking / deployment / analysis operator panels. */}
          <Route
            path="/creative-assets"
            element={
              <ProtectedRouteClient allowedRoles={["client", "creative"]}>
                <CreativeAssets />
              </ProtectedRouteClient>
            }
          />
          {/* Protected Beta surface: denied for every Cortex principal by
              CREATIVE_BETA_INTERNAL_ACCESS. The page component is not
              imported here, so its chunk never reaches a Cortex bundle. */}
          <Route
            path="/creative-trafficking"
            element={<BetaSurfaceRoute />}
          />
          {/* Protected Beta surface: denied for every Cortex principal by
              CREATIVE_BETA_INTERNAL_ACCESS. The page component is not
              imported here, so its chunk never reaches a Cortex bundle. */}
          <Route
            path="/creative-analysis"
            element={<BetaSurfaceRoute />}
          />
          {/* Protected Beta surface: denied for every Cortex principal by
              CREATIVE_BETA_INTERNAL_ACCESS. The page component is not
              imported here, so its chunk never reaches a Cortex bundle. */}
          <Route
            path="/creative-executions"
            element={<BetaSurfaceRoute />}
          />
          <Route
            path="/creative-studio"
            element={
              <ProtectedRouteClient allowedRoles={["client", "creative"]}>
                <CreativeStudio />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ClientApprovals />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/sherloc-media-planner"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <SherlocMediaPlannerClient />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/campaign-strategy/:campaignId"
            element={<CampaignStrategyMicrosite />}
          />
          <Route
            path="/campaign-strategy/:advertiserSlug/:campaignSlug"
            element={<CampaignStrategyMicrosite />}
          />
          {/* Public, unauthenticated, token-scoped analytics report */}
          <Route
            path="/report/:publicReportToken"
            element={<PublicPlatformReport />}
          />
          {/* Public, unauthenticated, token-scoped media campaign funding request */}
          <Route
            path="/fund/:fundingToken"
            element={<PublicFundingRequest />}
          />
          {/* Public, unauthenticated, token-scoped platform connection page */}
          <Route
            path="/connect/:onboardingToken"
            element={<PublicPlatformConnect />}
          />
          {/* Public, unauthenticated Pantheon experience */}
          <Route path="/pantheon" element={<PantheonPage />} />
          <Route
            path="/sherloc-media-campaigns"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <SherlocMediaCampaigns />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/sherloc-reports"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <SherlocReports />
              </ProtectedRouteClient>
            }
          />
          {/* Protected Beta surface: denied for every Cortex principal by
              CREATIVE_BETA_INTERNAL_ACCESS. The page component is not
              imported here, so its chunk never reaches a Cortex bundle. */}
          <Route
            path="/creative-optimus"
            element={<BetaSurfaceRoute />}
          />
          <Route
            path="/ai-optimizations"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <ClientAIOptimizations />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/optimus"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <Optimus />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <Integrations />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <Billing />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <Notifications />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <Settings />
              </ProtectedRouteClient>
            }
          />
          <Route
            path="/install-app"
            element={
              <ProtectedRouteClient allowedRoles={["client"]}>
                <InstallApp />
              </ProtectedRouteClient>
            }
          />

          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? landingPath : "/login"} replace />}
          />
          </Routes>

          {renderOverlay && <Loading active={showOverlay} />}
        </>
      </ErrorBoundary>
      {!shouldHideVoice && <GlobalVoiceController />}
    </>
  );
};

export default App;
